// src/FindGameModal.jsx - Fixed with proper real-time updates
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import socket from './socket';
import './FindGameModal.css';

export default function FindGameModal({ onClose }) {
  const [lobbies, setLobbies] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchMode, setSearchMode] = useState('browse'); // 'browse' or 'search'
  const [searchLobbyId, setSearchLobbyId] = useState('');
  const [searchError, setSearchError] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (searchMode === 'browse') {
      // Subscribe to real-time lobby updates
      if (!isSubscribed) {
        console.log('🔔 Subscribing to real-time lobby updates...');
        socket.emit('subscribeToLobbies');
        setIsSubscribed(true);
      }

      // Listen for real-time lobby updates
      const handleLobbiesUpdate = (updatedLobbies) => {
        console.log('📡 Received lobby update:', updatedLobbies);
        setLobbies(updatedLobbies);
        setIsLoading(false);
      };

      // Listen for specific lobby cancelled events
      const handleLobbyCancelled = (cancelledLobbyId) => {
        console.log('❌ Lobby cancelled:', cancelledLobbyId);
        setLobbies(prevLobbies => prevLobbies.filter(lobby => lobby.id !== cancelledLobbyId));
      };

      // Listen for lobby update events (player joins/leaves)
      const handleLobbyUpdate = (updatedLobby) => {
        console.log('🔄 Lobby updated:', updatedLobby.id);
        setLobbies(prevLobbies => {
          const index = prevLobbies.findIndex(l => l.id === updatedLobby.id);
          if (index >= 0) {
            const newLobbies = [...prevLobbies];
            // Only show if not full and not in game
            if (updatedLobby.players.length < updatedLobby.rules.players && !updatedLobby.gameStarted) {
              newLobbies[index] = updatedLobby;
            } else {
              // Remove if full or game started
              newLobbies.splice(index, 1);
            }
            return newLobbies;
          }
          // Add if new and available
          if (updatedLobby.players.length < updatedLobby.rules.players && !updatedLobby.gameStarted) {
            return [...prevLobbies, updatedLobby];
          }
          return prevLobbies;
        });
      };

      // Listen for game started events (remove from available lobbies)
      const handleGameStarted = ({ lobbyId }) => {
        console.log('🎮 Game started for lobby:', lobbyId);
        setLobbies(prevLobbies => prevLobbies.filter(lobby => lobby.id !== lobbyId));
      };

      socket.on('lobbiesUpdate', handleLobbiesUpdate);
      socket.on('lobbyCancelled', handleLobbyCancelled);
      socket.on('lobbyUpdate', handleLobbyUpdate);
      socket.on('gameStarted', handleGameStarted);

      // Initial fetch
      socket.emit('getLobbies', (availableLobbies) => {
        setLobbies(availableLobbies);
        setIsLoading(false);
      });

      // Cleanup function
      return () => {
        socket.off('lobbiesUpdate', handleLobbiesUpdate);
        socket.off('lobbyCancelled', handleLobbyCancelled);
        socket.off('lobbyUpdate', handleLobbyUpdate);
        socket.off('gameStarted', handleGameStarted);
      };
    }
  }, [searchMode, isSubscribed]);

  // Cleanup subscription when modal closes
  useEffect(() => {
    return () => {
      if (isSubscribed) {
        console.log('🔕 Unsubscribing from lobby updates...');
        socket.emit('unsubscribeFromLobbies');
      }
    };
  }, [isSubscribed]);

  const handleJoinLobby = (lobbyId) => {
    // When a user clicks "Join", navigate them to that lobby page
    navigate(`/lobby/${lobbyId}`);
    onClose();
  };

  const handleSearchById = async () => {
    if (!searchLobbyId.trim()) {
      setSearchError('Please enter a lobby ID');
      return;
    }

    setIsSearching(true);
    setSearchError('');

    socket.emit('joinLobby', searchLobbyId.trim(), (response) => {
      setIsSearching(false);
      
      if (response.error) {
        setSearchError(response.error);
      } else {
        // Successfully found and joined the lobby
        navigate(`/lobby/${searchLobbyId.trim()}`);
        onClose();
      }
    });
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearchById();
    }
  };

  const formatTimeAgo = (createdAt) => {
    const now = new Date();
    const created = new Date(createdAt);
    const diffInSeconds = Math.floor((now - created) / 1000);
    
    if (diffInSeconds < 60) return 'Just now';
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
    return `${Math.floor(diffInSeconds / 86400)}d ago`;
  };

  // Fixed function to get the correct game type display
  const getGameTypeDisplay = (lobby) => {
    // Check multiple possible property names for game type
    const gameType = lobby.rules?.type || lobby.rules?.gameType || lobby.type || lobby.gameType;
    
    // Normalize the game type for display
    if (!gameType || gameType.toLowerCase() === 'casual') {
      return 'CASUAL';
    } else if (gameType.toLowerCase() === 'ranked') {
      return 'RANKED';
    } else {
      return gameType.toUpperCase();
    }
  };

  // Get the appropriate badge color for game type
  const getGameTypeBadgeColor = (lobby) => {
    const gameType = lobby.rules?.type || lobby.rules?.gameType || lobby.type || lobby.gameType;
    
    if (gameType && gameType.toLowerCase() === 'ranked') {
      return '#f59e0b'; // Orange for ranked
    } else {
      return '#3b82f6'; // Blue for casual
    }
  };

  return (
    <div className="modal-backdrop">
      <div className="modal-content find-game-modal">
        <div className="modal-header">
          <h2>Find a Game</h2>
          {searchMode === 'browse' && (
            <div className="live-indicator">
              <div className="live-dot"></div>
              <span>LIVE</span>
            </div>
          )}
        </div>
        
        {/* Mode Toggle */}
        <div className="search-mode-toggle">
          <button
            className={`mode-btn ${searchMode === 'browse' ? 'active' : ''}`}
            onClick={() => setSearchMode('browse')}
          >
            <span className="mode-icon">🌍</span>
            Browse Public Games
          </button>
          <button
            className={`mode-btn ${searchMode === 'search' ? 'active' : ''}`}
            onClick={() => setSearchMode('search')}
          >
            <span className="mode-icon">🔍</span>
            Search by Lobby ID
          </button>
        </div>

        {/* Browse Mode */}
        {searchMode === 'browse' && (
          <div className="browse-section">
            <div className="lobby-list">
              {isLoading ? (
                <div className="loading-state">
                  <div className="loading-spinner"></div>
                  <p>Searching for open games...</p>
                </div>
              ) : lobbies.length > 0 ? (
                <>
                  <div className="lobby-count">
                    <span>{lobbies.length} game{lobbies.length !== 1 ? 's' : ''} available</span>
                    <div className="auto-refresh-indicator">
                      <div className="pulse-dot"></div>
                      Auto-updating
                    </div>
                  </div>
                  {lobbies.map(lobby => (
                    <div key={lobby.id} className="lobby-item">
                      <div className="lobby-info">
                        <div className="lobby-header">
                          <span className="host-name">🎮 {lobby.host.name}</span>
                          <span 
                            className="game-type-badge"
                            style={{ backgroundColor: getGameTypeBadgeColor(lobby) }}
                          >
                            {getGameTypeDisplay(lobby)}
                          </span>
                          <span className="time-ago">{formatTimeAgo(lobby.createdAt)}</span>
                        </div>
                        <div className="game-details">
                          <div className="detail-item">
                            <span className="detail-icon">👥</span>
                            <span className="player-count">
                              {lobby.players.length}/{lobby.rules.players} players
                            </span>
                          </div>
                          <div className="detail-item">
                            <span className="detail-icon">🎯</span>
                            <span className="pucks-info">
                              {lobby.rules.pucksPerTurn} pucks per turn
                            </span>
                          </div>
                          <div className="detail-item">
                            <span className="detail-icon">🔒</span>
                            <span className="privacy-info">
                              {lobby.rules.privacy}
                            </span>
                          </div>
                        </div>
                        {lobby.players.length > 1 && (
                          <div className="player-list">
                            <span className="players-label">Players:</span>
                            {lobby.players.map((player, index) => (
                              <span key={player.id} className="player-name">
                                {player.name}{index < lobby.players.length - 1 ? ', ' : ''}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                      <div className="lobby-actions">
                        <div className="lobby-status">
                          {lobby.players.length === lobby.rules.players ? (
                            <span className="status-full">Full</span>
                          ) : (
                            <span className="status-available">
                              {lobby.rules.players - lobby.players.length} slot{lobby.rules.players - lobby.players.length !== 1 ? 's' : ''} left
                            </span>
                          )}
                        </div>
                        <button 
                          className="btn btn-primary join-btn" 
                          onClick={() => handleJoinLobby(lobby.id)}
                          disabled={lobby.players.length >= lobby.rules.players}
                        >
                          {lobby.players.length >= lobby.rules.players ? 'Full' : 'Join Game'}
                        </button>
                      </div>
                    </div>
                  ))}
                </>
              ) : (
                <div className="no-games-state">
                  <div className="no-games-icon">🎮</div>
                  <h3>No Public Games Found</h3>
                  <p>There are no open public games right now. Why not create one or search for a specific lobby using its ID?</p>
                  <div className="auto-refresh-note">
                    <div className="pulse-dot"></div>
                    This list updates automatically when new games are created
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Search Mode */}
        {searchMode === 'search' && (
          <div className="search-section">
            <div className="search-info">
              <h3>Join by Lobby ID</h3>
              <p>Enter the lobby ID shared by your friend to join their game directly.</p>
            </div>
            
            <div className="search-input-container">
              <input
                type="text"
                value={searchLobbyId}
                onChange={(e) => setSearchLobbyId(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Enter lobby ID (e.g., lobby_abc123def)"
                className="search-input"
                disabled={isSearching}
              />
              <button
                onClick={handleSearchById}
                disabled={isSearching || !searchLobbyId.trim()}
                className="btn btn-primary search-btn"
              >
                {isSearching ? (
                  <>
                    <div className="loading-spinner small"></div>
                    Searching...
                  </>
                ) : (
                  'Join Lobby'
                )}
              </button>
            </div>

            {searchError && (
              <div className="search-error">
                ⚠️ {searchError}
              </div>
            )}

            <div className="search-tips">
              <h4>Tips:</h4>
              <ul>
                <li>Lobby IDs usually start with "lobby_" followed by random characters</li>
                <li>Make sure you have the complete ID (case sensitive)</li>
                <li>Private lobbies can only be joined with the exact ID</li>
                <li>The lobby must have available slots to join</li>
              </ul>
            </div>
          </div>
        )}

        <div className="modal-actions">
          <button type="button" className="btn btn-secondary" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
